import React, { useCallback, useEffect, useState } from "react";
import {
  createSeller,
  deleteSeller,
  getSellers,
  updateSeller,
} from "../utils/api.js";
import { Button, Card, Input, Badge } from "../components/legacy-ui.js";
import { AppPageHeader, AppTable } from "../components/app-ui.js";
import { Plus, Pencil, Trash2, Search, UserCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext.js";
import { Helmet } from "react-helmet";
import { companyPageTitle } from "../utils/company.js";
import toast from "react-hot-toast";
import { LoadingState } from "../components/motion.js";

const permissionSummary = (seller) => {
  const perms = seller.permissions || {};
  const labels = [];
  if (perms.manageInvoices) labels.push("Facturas");
  if (perms.managePayments) labels.push("Pagos");
  if (perms.createClients) labels.push("Crear clientes");
  return labels.length ? labels.join(" · ") : "Sin permisos extra";
};

const emptyPermissions = {
  manageInvoices: false,
  managePayments: false,
  createClients: false,
};

const PERMISSION_OPTIONS = [
  {
    key: "manageInvoices",
    label: "Gestionar facturas",
    hint: "Puede editar y eliminar facturas de sus clientes",
  },
  {
    key: "managePayments",
    label: "Gestionar pagos",
    hint: "Puede editar y eliminar pagos de sus facturas",
  },
  {
    key: "createClients",
    label: "Crear clientes",
    hint: "Puede registrar clientes y quedan asignados a su cuenta",
  },
];

const emptyForm = {
  username: "",
  fullName: "",
  password: "",
  isActive: true,
  permissions: { ...emptyPermissions },
};

const SellersModule = () => {
  const { user } = useAuth();
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSeller, setEditingSeller] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchSellers = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await getSellers({
        search: debouncedSearch || undefined,
        limit: 100,
      });
      setSellers(res.data.data || []);
    } catch (error) {
      console.error("Error fetching sellers:", error);
      toast.error(error.response?.data?.error || "No se pudieron cargar los vendedores", {
        position: "top-center",
      });
    } finally {
      setLoading(false);
    }
  }, [user, debouncedSearch]);

  useEffect(() => {
    fetchSellers();
  }, [fetchSellers]);

  const resetForm = () => {
    setShowForm(false);
    setEditingSeller(null);
    setFormData(emptyForm);
  };

  const handleAddNew = () => {
    setEditingSeller(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const handleEdit = (seller) => {
    setEditingSeller(seller);
    setFormData({
      username: seller.username || "",
      fullName: seller.fullName || "",
      password: "",
      isActive: seller.isActive !== false,
      permissions: {
        ...emptyPermissions,
        ...(seller.permissions || {}),
      },
    });
    setShowForm(true);
  };

  const handleDelete = async (sellerId) => {
    if (
      !window.confirm(
        "¿Eliminar este vendedor? Sus clientes quedarán sin vendedor asignado.",
      )
    ) {
      return;
    }
    try {
      await deleteSeller(sellerId);
      toast.success("Vendedor eliminado", { position: "top-center" });
      fetchSellers();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo eliminar el vendedor", {
        position: "top-center",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username.trim()) {
      toast.error("El usuario es obligatorio", { position: "top-center" });
      return;
    }
    if (!editingSeller && !formData.password) {
      toast.error("La contraseña es obligatoria", { position: "top-center" });
      return;
    }
    if (formData.password && formData.password.length < 4) {
      toast.error("La contraseña debe tener al menos 4 caracteres", { position: "top-center" });
      return;
    }

    const payload = {
      username: formData.username.trim(),
      fullName: formData.fullName.trim(),
      isActive: Boolean(formData.isActive),
      permissions: {
        manageInvoices: Boolean(formData.permissions.manageInvoices),
        managePayments: Boolean(formData.permissions.managePayments),
        createClients: Boolean(formData.permissions.createClients),
      },
    };
    if (formData.password) payload.password = formData.password;

    setSaving(true);
    try {
      if (editingSeller) {
        await updateSeller(editingSeller._id, payload);
        toast.success("Vendedor actualizado", { position: "top-center" });
      } else {
        await createSeller(payload);
        toast.success("Vendedor creado", { position: "top-center" });
      }
      resetForm();
      fetchSellers();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo guardar el vendedor", {
        position: "top-center",
      });
    } finally {
      setSaving(false);
    }
  };

  const SellerForm = (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Input
        label="Nombre completo"
        value={formData.fullName}
        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
        placeholder="Ej. María Pérez"
      />
      <Input
        label="Usuario *"
        required
        value={formData.username}
        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
        autoComplete="off"
      />
      <Input
        label={editingSeller ? "Nueva contraseña (opcional)" : "Contraseña *"}
        type="password"
        required={!editingSeller}
        value={formData.password}
        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
        autoComplete="new-password"
      />
      <label className="flex items-center gap-2 text-sm text-gray-700 mt-6">
        <input
          type="checkbox"
          checked={formData.isActive}
          onChange={(e) =>
            setFormData({ ...formData, isActive: e.target.checked })
          }
          className="rounded border-gray-300"
        />
        Cuenta activa
      </label>
      <div className="md:col-span-2 space-y-2">
        <p className="text-sm font-medium text-gray-700">Permisos</p>
        {PERMISSION_OPTIONS.map((option) => (
          <label key={option.key} className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={Boolean(formData.permissions[option.key])}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  permissions: {
                    ...formData.permissions,
                    [option.key]: e.target.checked,
                  },
                })
              }
              className="mt-0.5 rounded border-gray-300"
            />
            <span>
              <span className="font-medium">{option.label}</span>
              <span className="block text-xs text-gray-500">{option.hint}</span>
            </span>
          </label>
        ))}
      </div>
      <div className="md:col-span-2 flex gap-2 justify-end mt-2">
        <Button type="button" variant="secondary" onClick={resetForm}>
          Cancelar
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>
    </form>
  );

  return (
    <>
      <Helmet>
        <title>{companyPageTitle("Vendedores")}</title>
      </Helmet>
      <div className="app-page">
        <div className="app-section-head">
          <AppPageHeader
            title="Vendedores"
            subtitle="Crea cuentas de vendedor y asígnalas a clientes"
            actions={
              <Button onClick={handleAddNew}>
                <Plus size={16} /> Nuevo
              </Button>
            }
          />
          <div className="relative mt-4 mb-4">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Buscar por nombre o usuario..."
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm placeholder-gray-400"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="app-content">
          {showForm && (
            <Card className="p-4 mb-4 border border-indigo-100 bg-indigo-50/40">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <UserCheck size={16} />
                {editingSeller ? "Editar vendedor" : "Nuevo vendedor"}
              </h3>
              {SellerForm}
            </Card>
          )}

          {loading ? (
            <LoadingState label="Cargando vendedores" />
          ) : (
            <>
              <div className="md:hidden space-y-3">
                {sellers.map((seller) => (
                  <Card key={seller._id} className="p-4">
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <h3 className="font-bold text-gray-900">
                          {seller.fullName || seller.username}
                        </h3>
                        <p className="text-sm text-gray-500">@{seller.username}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {seller.clientCount || 0} cliente
                          {(seller.clientCount || 0) === 1 ? "" : "s"}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{permissionSummary(seller)}</p>
                      </div>
                      <Badge type={seller.isActive !== false ? "success" : "info"}>
                        {seller.isActive !== false ? "Activo" : "Inactivo"}
                      </Badge>
                    </div>
                    <div className="flex gap-2 justify-end mt-3">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(seller)}>
                        <Pencil size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(seller._id)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </Card>
                ))}
                {sellers.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-8">
                    No hay vendedores registrados
                  </p>
                )}
              </div>

              <div className="hidden md:block">
                <AppTable
                  columns={[
                    { key: "name", title: "Nombre" },
                    { key: "username", title: "Usuario" },
                    { key: "clients", title: "Clientes", align: "center" },
                    { key: "permissions", title: "Permisos" },
                    { key: "status", title: "Estado", align: "center" },
                    { key: "actions", title: "Acciones", align: "center" },
                  ]}
                >
                  {sellers.map((seller) => (
                    <tr key={seller._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3.5 text-sm font-medium text-gray-900">
                        {seller.fullName || "—"}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-gray-600">
                        {seller.username}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-center text-gray-600">
                        {seller.clientCount || 0}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-gray-600">
                        {permissionSummary(seller)}
                      </td>
                      <td className="px-6 py-3.5 text-center">
                        <Badge type={seller.isActive !== false ? "success" : "info"}>
                          {seller.isActive !== false ? "Activo" : "Inactivo"}
                        </Badge>
                      </td>
                      <td className="px-6 py-3.5 text-center">
                        <div className="flex gap-1 justify-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(seller)}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(seller._id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </AppTable>
                {sellers.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-8">
                    No hay vendedores registrados
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SellersModule;
